import { Button, Card, Cluster, Container, Pill, Section, Stack, Status } from "@humaniti/ui";
import { Link } from "react-router-dom";
import { ModalityTabs } from "../components/ModalityTabs";
import { MODALITIES, TASK_TYPE_COUNT } from "../content/taskTypes";
import { SEO } from "../lib/seo";

const REASONS = [
  {
    n: "01",
    title: "Direct pay",
    body: "Per verified label, not per hour. Settlements weekly. Signed receipts land in Phase 2.",
  },
  {
    n: "02",
    title: "Verified accuracy unlocks tiers",
    body: "Your accuracy raises your tier. Higher tiers unlock higher-paying work.",
  },
  {
    n: "03",
    title: "Real anonymity at T1",
    body: "Phone-only at T1. ID verification only when payouts justify it.",
  },
  {
    n: "04",
    title: "Web + mobile",
    body: "Complex tasks on web. Swipe-style classification on Android.",
  },
];

const TIERS: { tier: string; label: string; body: string }[] = [
  {
    tier: "T0",
    label: "Signed up",
    body: "Account created. Browse the network. No tasks yet.",
  },
  {
    tier: "T1",
    label: "Phone verified",
    body: "Pick up low-stakes work. Per-label rates start here.",
  },
  {
    tier: "T2",
    label: "ID verified",
    body: "Standard work and higher rates. Sumsub liveness and ID check.",
  },
  {
    tier: "T3",
    label: "Reputation verified",
    body: "Sensitive work. Steward eligibility. Highest rates.",
  },
];

// Task-type list lives in src/content/taskTypes.ts so PricingPage and the
// "17 task types" sections on ForBuildersPage + ForHumansPage stay in sync.
// Imported below as MODALITIES + TASK_TYPE_COUNT.

export const ForHumansPage = () => (
  <>
    <SEO
      title="For Humans — Get paid for verified work"
      description="Sign up. Verify. Pick up tasks. Get paid per verified label. Become one of the Humans of Humaniti."
      path="/for-humans"
    />
    {/* 1. Hero */}
    <Section>
      <Container size="full">
        <Stack gap="2xl">
          <Pill variant="accent">For Humans</Pill>
          <h1
            style={{
              margin: 0,
              fontFamily: "var(--font-sans)",
              fontWeight: 500,
              fontSize: "var(--fs-display)",
              lineHeight: "var(--lh-display)",
              letterSpacing: "-0.02em",
              maxWidth: 960,
            }}
          >
            Get paid for verified work<span style={{ color: "var(--teal)" }}>.</span>
          </h1>
          <p
            style={{
              margin: 0,
              maxWidth: 620,
              fontSize: "var(--fs-body-lg)",
              lineHeight: "var(--lh-body-lg)",
              color: "var(--fg-secondary)",
            }}
          >
            Sign up. Verify. Pick up tasks. Get paid per verified label.
          </p>
          <Cluster gap="lg">
            {/* All Earn as a Human CTAs route through /coming-soon until M1.6
                launch — see ComingSoonPage.tsx for the rationale. */}
            <Link to="/coming-soon?for=humans" style={{ textDecoration: "none" }}>
              <Button>Earn as a Human →</Button>
            </Link>
          </Cluster>
        </Stack>
      </Container>
    </Section>

    {/* 2. Why Humans stay — numbered editorial */}
    <Section style={{ borderTop: "1px solid var(--slate-300)" }}>
      <Container size="full">
        <div className="marketing-side-grid">
          <div>
            <span
              style={{
                fontFamily: "var(--font-mono)",
                fontSize: "var(--fs-label)",
                letterSpacing: "0.08em",
                textTransform: "uppercase",
                color: "var(--fg-secondary)",
              }}
            >
              § 02 — Why us
            </span>
            <h2
              style={{
                margin: "var(--space-4) 0 0",
                fontFamily: "var(--font-sans)",
                fontWeight: 500,
                fontSize: "var(--fs-h1)",
                lineHeight: "var(--lh-h1)",
                letterSpacing: "-0.01em",
              }}
            >
              Why Humans
              <span style={{ color: "var(--teal)" }}>/</span>
              <br />
              stay
            </h2>
          </div>
          <ol style={{ listStyle: "none", margin: 0, padding: 0 }}>
            {REASONS.map((r) => (
              <li
                key={r.n}
                style={{
                  display: "grid",
                  gridTemplateColumns: "64px 1fr",
                  gap: "var(--space-5)",
                  padding: "var(--space-6) 0",
                  borderTop: "1px solid var(--border-hairline)",
                }}
              >
                <div
                  style={{
                    fontFamily: "var(--font-mono)",
                    fontSize: "var(--fs-small)",
                    color: "var(--fg-secondary)",
                    paddingTop: "var(--space-1)",
                  }}
                >
                  {r.n}
                </div>
                <div>
                  <div
                    style={{
                      fontFamily: "var(--font-sans)",
                      fontWeight: 500,
                      fontSize: "22px",
                      lineHeight: "32px",
                      marginBottom: "var(--space-2)",
                    }}
                  >
                    {r.title}.
                  </div>
                  <div
                    style={{
                      fontSize: "var(--fs-body)",
                      lineHeight: "var(--lh-body)",
                      color: "var(--fg-secondary)",
                      maxWidth: 560,
                    }}
                  >
                    {r.body}
                  </div>
                </div>
              </li>
            ))}
          </ol>
        </div>
      </Container>
    </Section>

    {/* 3. Qualification path — visual ladder */}
    <Section style={{ borderTop: "1px solid var(--slate-300)" }}>
      <Container size="full">
        <Stack gap="lg">
          <span
            style={{
              fontFamily: "var(--font-mono)",
              fontSize: "var(--fs-label)",
              letterSpacing: "0.08em",
              textTransform: "uppercase",
              color: "var(--fg-secondary)",
            }}
          >
            § 03 — The path
          </span>
          <h2
            style={{
              margin: 0,
              fontFamily: "var(--font-sans)",
              fontWeight: 500,
              fontSize: "var(--fs-h1)",
              lineHeight: "var(--lh-h1)",
              letterSpacing: "-0.01em",
            }}
          >
            From applicant to Steward<span style={{ color: "var(--teal)" }}>.</span>
          </h2>
          <Cluster gap="md" align="start">
            {TIERS.map((t) => (
              <Card key={t.tier} style={{ flex: 1, minWidth: 220 }}>
                <Status variant="accent">{t.tier}</Status>
                <h3
                  style={{
                    margin: "var(--space-3) 0 var(--space-2)",
                    fontSize: "var(--fs-h3)",
                    fontFamily: "var(--font-sans)",
                    fontWeight: 500,
                  }}
                >
                  {t.label}
                </h3>
                <p
                  style={{
                    margin: 0,
                    color: "var(--fg-secondary)",
                    fontSize: "var(--fs-body)",
                    lineHeight: "var(--lh-body)",
                  }}
                >
                  {t.body}
                </p>
              </Card>
            ))}
          </Cluster>
        </Stack>
      </Container>
    </Section>

    {/* 4. Task type grid */}
    <Section style={{ borderTop: "1px solid var(--slate-300)" }}>
      <Container size="full">
        <Stack gap="lg">
          <span
            style={{
              fontFamily: "var(--font-mono)",
              fontSize: "var(--fs-label)",
              letterSpacing: "0.08em",
              textTransform: "uppercase",
              color: "var(--fg-secondary)",
            }}
          >
            § 04 — What you'll do
          </span>
          <h2
            style={{
              margin: 0,
              fontFamily: "var(--font-sans)",
              fontWeight: 500,
              fontSize: "var(--fs-h1)",
              lineHeight: "var(--lh-h1)",
              letterSpacing: "-0.01em",
            }}
          >
            {TASK_TYPE_COUNT} task types. Pick yours<span style={{ color: "var(--teal)" }}>.</span>
          </h2>
          <p
            style={{
              margin: 0,
              fontSize: "var(--fs-body-lg)",
              lineHeight: "var(--lh-body-lg)",
              maxWidth: 720,
              color: "var(--fg-secondary)",
            }}
          >
            {TASK_TYPE_COUNT} task types across {MODALITIES.length} modalities. Different skills,
            different tools, different rates. Pick what fits.
          </p>
          {/* Tabbed coverage shared with ForBuildersPage. See ModalityTabs.tsx
              for the layout notes (notably the explicit width: 100% needed to
              defeat Stack's align-items: flex-start default). */}
          <ModalityTabs />
        </Stack>
      </Container>
    </Section>

    {/* 5. The pay */}
    <Section style={{ borderTop: "1px solid var(--slate-300)" }}>
      <Container size="full">
        <Stack gap="md">
          <span
            style={{
              fontFamily: "var(--font-mono)",
              fontSize: "var(--fs-label)",
              letterSpacing: "0.08em",
              textTransform: "uppercase",
              color: "var(--fg-secondary)",
            }}
          >
            § 05 — The pay
          </span>
          <h2
            style={{
              margin: 0,
              fontFamily: "var(--font-sans)",
              fontWeight: 500,
              fontSize: "var(--fs-h1)",
              lineHeight: "var(--lh-h1)",
              letterSpacing: "-0.01em",
            }}
          >
            How much. How often. How<span style={{ color: "var(--teal)" }}>.</span>
          </h2>
          <p
            style={{
              margin: 0,
              fontSize: "var(--fs-body-lg)",
              lineHeight: "var(--lh-body-lg)",
              maxWidth: 720,
              color: "var(--fg-secondary)",
            }}
          >
            Per-label rate ranges live on the pricing page. Settlements weekly. Payment via Wise,
            Razorpay, Rapyd, Stripe Connect, or fiat to USDC (Transak, Circle).
          </p>
          <Link to="/pricing">See the full pricing grid →</Link>
        </Stack>
      </Container>
    </Section>

    {/* 6. Real talk */}
    <Section style={{ borderTop: "1px solid var(--slate-300)" }}>
      <Container size="full">
        <Stack gap="md">
          <span
            style={{
              fontFamily: "var(--font-mono)",
              fontSize: "var(--fs-label)",
              letterSpacing: "0.08em",
              textTransform: "uppercase",
              color: "var(--fg-secondary)",
            }}
          >
            § 06 — Real talk
          </span>
          <h2
            style={{
              margin: 0,
              fontFamily: "var(--font-sans)",
              fontWeight: 500,
              fontSize: "var(--fs-h1)",
              lineHeight: "var(--lh-h1)",
              letterSpacing: "-0.01em",
            }}
          >
            What this isn't<span style={{ color: "var(--teal)" }}>.</span>
          </h2>
          <p
            style={{
              margin: 0,
              fontSize: "var(--fs-body-lg)",
              lineHeight: "var(--lh-body-lg)",
              maxWidth: 720,
              color: "var(--fg-secondary)",
            }}
          >
            Humaniti is not gig work. Not a get-rich app. Not anonymous past T1. Not a guarantee of
            task volume. The Network rewards verified accuracy and steady contribution. If that's
            not what you're looking for, this isn't it.
          </p>
        </Stack>
      </Container>
    </Section>

    {/* 7. Closing CTA */}
    <Section style={{ background: "var(--ink)", color: "var(--paper)" }}>
      <Container size="full">
        <Stack gap="lg" alignItems="center" style={{ textAlign: "center" }}>
          <h2
            style={{
              margin: 0,
              fontFamily: "var(--font-sans)",
              fontWeight: 500,
              fontSize: "var(--fs-h1)",
              lineHeight: "var(--lh-h1)",
              letterSpacing: "-0.01em",
            }}
          >
            Apply now<span style={{ color: "var(--teal)" }}>.</span>
          </h2>
          <Link to="/coming-soon?for=humans" style={{ textDecoration: "none" }}>
            <Button>Earn as a Human →</Button>
          </Link>
        </Stack>
      </Container>
    </Section>
  </>
);
